import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { LayoutDashboard, Package, ShoppingCart, Users, Image, MapPin, ChevronLeft, Menu, X, LogOut, RotateCcw, Settings } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { BizInfoProvider } from '../../contexts/BizInfoContext'; // ← ADD THIS
import { t } from '../../lib/translations';

const menuItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'dashboard' },
  { path: '/admin/products', icon: Package, label: 'products' },
  { path: '/admin/categories', icon: Package, label: 'categories' },
  { path: '/admin/orders', icon: ShoppingCart, label: 'orders' },
  { path: '/admin/returns', icon: RotateCcw, label: 'returns' },
  { path: '/admin/customers', icon: Users, label: 'customers' },
  { path: '/admin/banners', icon: Image, label: 'banners' },
  { path: '/admin/pincodes', icon: MapPin, label: 'pincodes' },
  { path: '/admin/settings', icon: Settings, label: 'settings' },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, language, logout, isAdmin, isAuthenticated } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    } else if (!isAdmin) {
      navigate('/');
    }
  }, [isAuthenticated, isAdmin, navigate]);

  if (!isAdmin) return null;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <BizInfoProvider> {/* ← ADD THIS WRAPPER */}
      <div className="min-h-screen bg-slate-100" data-testid="admin-layout">
        {/* Mobile Header */}
        <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-slate-900 text-white flex items-center justify-between px-4 z-50">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">AV</span>
            </div>
            <span className="font-bold">Admin</span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white hover:bg-slate-800"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </header>

        {/* Sidebar */}
        <aside className={`admin-sidebar fixed inset-y-0 left-0 w-64 transform lg:transform-none transition-transform z-40 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}>
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-slate-700">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">AV</span>
              </div>
              <div>
                <span className="text-white font-bold">AV Mart</span>
                <span className="text-slate-400 text-xs block">Admin Panel</span>
              </div>
            </Link>
          </div>

          {/* Menu */}
          <nav className="p-4 space-y-1">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-green-600 text-white'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                  data-testid={`admin-nav-${item.label}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{t(item.label, language)}</span>
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700">
            <div className="flex items-center gap-3 px-4 py-2 text-slate-400 mb-2">
              <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                <Users className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm truncate">{user?.name}</p>
                <p className="text-xs truncate">{user?.email}</p>
              </div>
            </div>
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-2 text-slate-400 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>{language === 'hi' ? 'स्टोर पर वापस' : 'Back to Store'}</span>
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-2 text-red-400 hover:text-red-300 transition-colors w-full"
              data-testid="admin-logout"
            >
              <LogOut className="w-5 h-5" />
              <span>{t('logout', language)}</span>
            </button>
          </div>
        </aside>

        {/* Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
          <div className="p-4 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </BizInfoProvider>
  );
}
